// v14
// Path: frontend/src/components/SyncHub/index.jsx
// Inputs: token (JWT authorization string)
// Outputs: React JSX single-page consolidated master-detail interface for QuickBooks integrations
// Summary: Single-page unified management hub for Passport ERP to QuickBooks Online, featuring standard presets, embedded mapping designer with inspect/diff and sync runner, canonical target entities, global dialog mounting, connection group controls, and direct integration with Data Catalog Studio & Vault.

import React, { useState, useEffect, useMemo } from 'react';
import { FieldMappingDesigner } from './FieldMappingDesigner';
import { QUICKBOOKS_TARGET_ENTITIES, getQbEntityMeta, normalizeEntityName } from './entityCatalogs';
import { FieldMappingModal } from './FieldMappingModal';
import { ConnectionDetailsModal } from './ConnectionDetailsModal';
import { ConnectionLogsModal } from './ConnectionLogsModal';
import { DryRunDiffModal } from './DryRunDiffModal';
import { PipelineConfigModal } from './PipelineConfigModal';
import { IntegrationSettingsModal } from './IntegrationSettingsModal';
import { DataDictionaryModal } from './DataDictionaryModal';
import { EditConnectionGroupModal } from './EditConnectionGroupModal';
import { StandardMappingsModal } from './StandardMappingsModal';
import { ApplyStandardMappingsModal } from './ApplyStandardMappingsModal';
import DataCatalogStudio from './DataCatalogStudio';
import GlobalDialogs from './shared/GlobalDialogs';


const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

import { useSyncStore } from './useSyncStore';

export default function QuickBooksSync({ token }) {
    const syncStore = useSyncStore();
    useEffect(() => {
        if (token) {
            syncStore.setToken(token);
            syncStore.fetchAll();
        }
    }, [token]);

    const [accounts, setAccounts] = useState([]);
    const [configs, setConfigs] = useState([]);
    const [dbProfiles, setDbProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);
    const [editingGroup, setEditingGroup] = useState(null);

    // Expandable accordion state (keyed by connection group key)
    const [expandedConnections, setExpandedConnections] = useState({});
    const [selectedEntityByGroup, setSelectedEntityByGroup] = useState({});

    // Modals state
    const [mappingConfig, setMappingConfig] = useState(null);
    const [diffConfig, setDiffConfig] = useState(null);
    const [detailsAccount, setDetailsAccount] = useState(null);
    const [logsAccount, setLogsAccount] = useState(null);
    const [pipelineConfig, setPipelineConfig] = useState(null);
    const [pipelineDefaults, setPipelineDefaults] = useState({});
    const [showPipelineModal, setShowPipelineModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showStandardMappingsModal, setShowStandardMappingsModal] = useState(false);
    const [applyMappingsGroup, setApplyMappingsGroup] = useState(null);
    const [showDictionaryModal, setShowDictionaryModal] = useState(false);
    const [showCatalogStudio, setShowCatalogStudio] = useState(false);
    const [accountEntities, setAccountEntities] = useState({});

    // Filter/Search
    const [searchQuery, setSearchQuery] = useState('');
    const [envFilter, setEnvFilter] = useState('ALL');

    const loadData = async () => {
        setLoading(true);
        try {
            const authHeaders = { 'Authorization': `Bearer ${token}` };
            const [accRes, cfgRes, profRes] = await Promise.all([
                fetch('/api/sync/accounts', { headers: authHeaders }),
                fetch('/api/sync/configs', { headers: authHeaders }),
                fetch('/api/sync/database-profiles', { headers: authHeaders })
            ]);

            if (accRes.ok) setAccounts(await unbox(accRes));
            if (cfgRes.ok) setConfigs(await unbox(cfgRes));
            if (profRes.ok) setDbProfiles(await unbox(profRes));
        } catch (err) {
            console.error('[QuickBooksSync] Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();

        // Check OAuth query params
        const params = new URLSearchParams(window.location.search);
        if (params.get('connected') === 'true') {
            setNotification({
                type: 'success',
                message: 'Successfully authorized and connected with QuickBooks Online!'
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (params.get('error')) {
            setNotification({
                type: 'error',
                message: `OAuth Authorization Error: ${params.get('error')}`
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [token]);

    // Fetch available target entities for QuickBooks accounts
    useEffect(() => {
        accounts.forEach(acc => {
            if (acc.AccountID && !accountEntities[acc.AccountID]) {
                fetch(`/api/sync/accounts/${acc.AccountID}/entities`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                .then(res => res.ok ? unbox(res) : null)
                .then(data => {
                    if (data && Array.isArray(data.entities)) {
                        setAccountEntities(prev => ({
                            ...prev,
                            [acc.AccountID]: data.entities
                        }));
                    }
                })
                .catch(err => console.warn('Could not fetch entities for account', acc.AccountID, err));
            }
        });
    }, [accounts, token]);


    // Group configurations into unified Connection entries
    const connections = useMemo(() => {
        const groups = {};

        // 1. Group by configured pipelines
        configs.forEach(cfg => {
            const matchedProfile = dbProfiles.find(p => p.connPrefix === cfg.SourceConnPrefix);
            const profileDisplayName = matchedProfile ? matchedProfile.displayName : (cfg.SourceConnPrefix || 'Local Database Profile');
            const dbName = cfg.SourceDatabaseName || 'pbsdata00';
            const accKey = cfg.AccountID || 'unassigned';
            const groupKey = `${cfg.SourceConnPrefix || 'LOCAL'}__@@__${dbName}__@@__${accKey}`;

            if (!groups[groupKey]) {
                const matchedAccount = accounts.find(a => String(a.AccountID) === String(cfg.AccountID));
                groups[groupKey] = {
                    groupKey,
                    sourceConnPrefix: cfg.SourceConnPrefix || '',
                    profileDisplayName,
                    sourceDatabase: dbName,
                    accountId: cfg.AccountID,
                    account: matchedAccount || null,
                    accountName: cfg.AccountName || matchedAccount?.AccountName || `Account #${cfg.AccountID}`,
                    accountEnvironment: cfg.Environment || matchedAccount?.Environment || 'sandbox',
                    accountConnected: cfg.AccountConnected !== undefined ? cfg.AccountConnected : (matchedAccount?.IsConnected || false),
                    companyName: matchedAccount?.CompanyName || '',
                    pipelines: []
                };
            }

            groups[groupKey].pipelines.push(cfg);
        });

        // 2. Also include any registered QuickBooks accounts that do not have pipelines yet
        accounts.forEach(acc => {
            const hasGroup = Object.values(groups).some(g => String(g.accountId) === String(acc.AccountID));
            if (!hasGroup) {
                const groupKey = `UNLINKED__@@__pbsdata00__@@__${acc.AccountID}`;
                groups[groupKey] = {
                    groupKey,
                    sourceConnPrefix: dbProfiles[0]?.connPrefix || '',
                    profileDisplayName: dbProfiles[0]?.displayName || 'Select Profile',
                    sourceDatabase: 'pbsdata00',
                    accountId: acc.AccountID,
                    account: acc,
                    accountName: acc.AccountName,
                    accountEnvironment: acc.Environment,
                    accountConnected: acc.IsConnected,
                    companyName: acc.CompanyName || '',
                    pipelines: []
                };
            }
        });

        return Object.values(groups);
    }, [configs, accounts, dbProfiles]);

    // Filter connections
    const filteredConnections = useMemo(() => {
        return connections.filter(conn => {
            if (envFilter !== 'ALL' && conn.accountEnvironment !== envFilter) return false;
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const matchName = conn.accountName.toLowerCase().includes(q);
                const matchDb = conn.sourceDatabase.toLowerCase().includes(q);
                const matchProf = conn.profileDisplayName.toLowerCase().includes(q);
                const matchComp = conn.companyName.toLowerCase().includes(q);
                if (!matchName && !matchDb && !matchProf && !matchComp) return false;
            }
            return true;
        });
    }, [connections, envFilter, searchQuery]);

    const toggleConnectionExpand = (groupKey) => {
        setExpandedConnections(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };


    const handleDeleteAccount = async (accountId) => {
        if (!(await useSyncStore.getState().showConfirm('Are you sure you want to delete this QuickBooks connection? All linked configurations will be unassigned.'))) return;
        try {
            const res = await fetch(`/api/sync/accounts/${accountId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                loadData();
            } else {
                const err = await unbox(res);
                useSyncStore.getState().showAlert(`Failed to delete account: ${err.error || 'Server error'}`);
            }
        } catch (err) {
            useSyncStore.getState().showAlert(`Network error: ${err.message}`);
        }
    };

    const getMappedFieldCount = (cfg) => {
        if (!cfg) return 0;
        try {
            const raw = cfg.FieldMappingsJson || cfg.FieldMappings;
            const mappings = typeof raw === 'string' ? JSON.parse(raw) : (raw || []);
            if (!Array.isArray(mappings)) return 0;
            return mappings.filter(m => {
                if (!m || !m.target) return false;
                if (m.source && String(m.source).trim()) return true;
                if (Array.isArray(m.sources) && m.sources.length > 0) return true;
                if (m.template && String(m.template).trim()) return true;
                return false;
            }).length;
        } catch (e) {
            return 0;
        }
    };

    const findPipelineForEntity = (pipelines, ent) => {
        if (!Array.isArray(pipelines) || !ent) return null;
        const entName = typeof ent === 'string' ? ent : (ent.name || ent.entity || String(ent));
        const targetNorm = (entName || '').toLowerCase().trim();
        if (!targetNorm) return null;
        return pipelines.find(p => {
            if (!p) return false;
            const pTarget = (p.TargetQBEntity || p.EntityType || '').toLowerCase().trim();
            if (pTarget === targetNorm) return true;
            if (targetNorm === 'customer' && (pTarget === 'customers' || pTarget === 'customer')) return true;
            if (targetNorm === 'item' && (pTarget === 'items' || pTarget === 'item')) return true;
            if (targetNorm === 'vendor' && (pTarget === 'vendors' || pTarget === 'vendor')) return true;
            if (targetNorm === 'account' && (pTarget === 'accounts' || pTarget === 'account')) return true;
            if (targetNorm === 'invoice' && (pTarget === 'invoices' || pTarget === 'invoice' || pTarget === 'order' || pTarget === 'orders')) return true;
            if (targetNorm === 'bill' && (pTarget === 'bills' || pTarget === 'bill')) return true;
            if (targetNorm === 'payment' && (pTarget === 'payments' || pTarget === 'payment')) return true;
            if (targetNorm === 'journalentry' && (pTarget === 'journalentries' || pTarget === 'journal entry' || pTarget === 'journalentry')) return true;
            if (targetNorm === 'purchaseorder' && (pTarget === 'purchaseorders' || pTarget === 'purchase order' || pTarget === 'purchaseorder')) return true;
            if (targetNorm === 'class' && (pTarget === 'classes' || pTarget === 'class')) return true;
            if (targetNorm === 'department' && (pTarget === 'departments' || pTarget === 'department' || pTarget === 'location' || pTarget === 'locations')) return true;
            return false;
        });
    };

    const handlePillClick = (conn, entName) => {
        setExpandedConnections(prev => ({ ...prev, [conn.groupKey]: true }));
        setSelectedEntityByGroup(prev => ({ ...prev, [conn.groupKey]: entName }));
    };

    const handleToggleActive = async (cfg) => {
        const newStatus = !cfg.IsActive;
        try {
            const res = await fetch('/api/sync/configs/toggle-active', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    configId: cfg.ConfigID,
                    isActive: newStatus
                })
            });
            if (res.ok) {
                setConfigs(prev => prev.map(c => c.ConfigID === cfg.ConfigID ? { ...c, IsActive: newStatus } : c));
            } else {
                const data = await unbox(res);
                useSyncStore.getState().showAlert(`Failed to toggle status: ${data.error || 'Server error'}`);
            }
        } catch (err) {
            useSyncStore.getState().showAlert(`Failed to toggle active: ${err.message}`);
        }
    };

    const handleInitializePipeline = async (conn, entName) => {
        const canonicalName = normalizeEntityName(entName);
        const meta = getQbEntityMeta(canonicalName);
        let tbl = meta.defaultTable || 'PBS';
        let initialMappings = null;

        setLoading(true);
        try {
            // Fetch standard preset mappings for this entity if available
            try {
                const stdRes = await fetch('/api/sync/standard-mappings?sourceSystem=PBS', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (stdRes.ok) {
                    const stdList = await unbox(stdRes);
                    const matched = stdList.find(s => normalizeEntityName(s.TargetQBEntity || s.EntityType) === canonicalName);
                    if (matched) {
                        if (matched.SourceTable) tbl = matched.SourceTable;
                        if (matched.FieldMappingsJson) {
                            initialMappings = typeof matched.FieldMappingsJson === 'string' ? JSON.parse(matched.FieldMappingsJson) : matched.FieldMappingsJson;
                        }
                    }
                }
            } catch (stdErr) {
                console.warn('[QuickBooksSync] Could not pre-fetch standard preset:', stdErr);
            }

            const res = await fetch('/api/sync/configs', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    configName: `PBS ${canonicalName} (${conn.sourceDatabase}) -> QBO`,
                    accountId: parseInt(conn.accountId, 10),
                    sourceConnPrefix: conn.sourceConnPrefix,
                    sourceDatabaseName: conn.sourceDatabase,
                    tableName: tbl,
                    entityType: canonicalName,
                    targetQBEntity: canonicalName,
                    syncDirection: 'PBS_TO_QB',
                    conflictPolicy: 'SOURCE_WINS',
                    isActive: true,
                    fieldMappings: initialMappings
                })
            });
            if (res.ok) {
                await loadData();
                setExpandedConnections(prev => ({ ...prev, [conn.groupKey]: true }));
                setSelectedEntityByGroup(prev => ({ ...prev, [conn.groupKey]: canonicalName }));
            } else {
                const err = await unbox(res);
                useSyncStore.getState().showAlert(`Failed to initialize pipeline: ${err.error || 'Unknown error'}`);
            }
        } catch (err) {
            useSyncStore.getState().showAlert(`Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRunLiveSyncQuick = async (cfg) => {
        const confirmMsg = `Confirm LIVE Synchronization Settings:\n\n` +
            `• SQL Instance: ${cfg.SourceConnPrefix || 'Local SQL'}\n` +
            `• Source DB & Table: [${cfg.SourceDatabaseName}].[dbo].[${cfg.TableName || cfg.EntityType}]\n` +
            `• QuickBooks Target: ${cfg.AccountName || 'Account #' + cfg.AccountID} (${cfg.TargetQBEntity || cfg.EntityType})\n` +
            `• Direction & Policy: ${cfg.SyncDirection} (${cfg.ConflictPolicy})\n` +
            `• Batch Limit: 50 records\n\n` +
            `Proceed with live batch execution now?`;
        if (!(await useSyncStore.getState().showConfirm(confirmMsg))) return;
        try {
            const res = await fetch('/api/sync/sync/execute', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    configId: cfg.ConfigID,
                    accountId: cfg.AccountID,
                    sourceConnPrefix: cfg.SourceConnPrefix,
                    sourceDatabaseName: cfg.SourceDatabaseName,
                    tableName: cfg.TableName,
                    targetQBEntity: cfg.TargetQBEntity,
                    entityType: cfg.EntityType,
                    limit: 50
                })
            });
            const data = await unbox(res);
            if (res.ok) {
                if (data.errors && data.errors.length > 0) {
                    const errSnippet = data.errors.slice(0, 4).map(e => `• [${e.key}]${e.name ? ` (${e.name})` : ''}: ${e.error}`).join('\n');
                    const more = data.errors.length > 4 ? `\n...and ${data.errors.length - 4} more error(s).` : '';
                    useSyncStore.getState().showAlert(`Sync Result (${data.status}):\n${data.summary}\n\nFailed Items Diagnostics:\n${errSnippet}${more}\n\nCheck the Execution Logs tab for complete details.`);
                } else {
                    useSyncStore.getState().showAlert(`Sync Completed (${data.status}):\n${data.summary}`);
                }
                loadData();
            } else {
                useSyncStore.getState().showAlert(`Sync Error: ${data.error || 'Execution failed'}`);
            }
        } catch (err) {
            useSyncStore.getState().showAlert(`Sync Failed: ${err.message}`);
        }
    };

    const handleDeletePipeline = async (configId) => {
        if (!(await useSyncStore.getState().showConfirm('Are you sure you want to delete this table sync pipeline?'))) return;
        try {
            const res = await fetch(`/api/sync/configs/${configId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) loadData();
        } catch (err) {
            useSyncStore.getState().showAlert(`Delete failed: ${err.message}`);
        }
    };

    const getEntityIcon = (entity) => {
        switch (entity) {
            case 'Customers': return { icon: 'group', table: 'CUSFIL' };
            case 'Items': return { icon: 'inventory_2', table: 'ITMFIL' };
            case 'Accounts': return { icon: 'account_tree', table: 'CHARTF' };
            case 'Vendors': return { icon: 'store', table: 'VENFIL' };
            default: return { icon: 'table_chart', table: 'PBS' };
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-900 overflow-y-auto p-6 space-y-6">
            {/* Top Header Banner */}
            <div className="flex flex-wrap justify-between items-center gap-4 bg-gradient-to-r from-slate-800 via-slate-800 to-slate-850 p-5 rounded-xl border border-slate-700 shadow-md">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 shadow-inner">
                        <span className="material-symbols-outlined text-[28px]">sync_alt</span>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-white tracking-wide">Passport to QuickBooks Sync Hub</h2>
                            <span className="px-2.5 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full text-xs font-semibold">
                                Live Integration
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                            Single-line connection overview with table sync pipelines, interactive field mappings, and audit diagnostics.
                        </p>
                    </div>
                </div>

                {/* Top Action Buttons */}
                <div className="flex items-center gap-2.5 flex-wrap">
                    <button
                        onClick={() => {
                            setPipelineDefaults({});
                            setPipelineConfig(null);
                            setShowPipelineModal(true);
                        }}
                        className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                    >
                        <span className="material-symbols-outlined text-[16px]">add_link</span>
                        + New Connection Group
                    </button>
                    <button
                        onClick={() => setShowSettingsModal(true)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold transition-colors border border-slate-700 shadow-sm"
                        title="Configure default root table names and QB entity destinations"
                    >
                        <span className="material-symbols-outlined text-[16px] text-amber-400">tune</span>
                        Environmental Settings
                    </button>
                    <button
                        onClick={() => setShowStandardMappingsModal(true)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600/80 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold transition-colors border border-indigo-500/40 shadow-sm"
                        title="Manage and customize standard field mapping presets across systems"
                    >
                        <span className="material-symbols-outlined text-[16px]">schema</span>
                        Standard Mappings
                    </button>
                    <button
                        onClick={() => setShowCatalogStudio(true)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-semibold transition-all border border-emerald-400/40 shadow-sm"
                        title="Open Data Catalog Studio: Connections, Transforms, Entities, Fields & Relationships"
                    >
                        <span className="material-symbols-outlined text-[16px]">menu_book</span>
                        Data Catalog Studio
                    </button>
                    <button
                        onClick={loadData}
                        disabled={loading}
                        className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors border border-slate-700"
                        title="Refresh all data"
                    >
                        <span className={`material-symbols-outlined text-[18px] ${loading ? 'animate-spin' : ''}`}>sync</span>
                    </button>
                </div>
            </div>

            {/* Notification Banner */}
            {notification && (
                <div className={`p-4 rounded-xl border text-sm flex items-center justify-between ${notification.type === 'success' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-200' : 'bg-red-950/60 border-red-800 text-red-200'}`}>
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined">
                            {notification.type === 'success' ? 'check_circle' : 'error'}
                        </span>
                        <span>{notification.message}</span>
                    </div>
                    <button onClick={() => setNotification(null)} className="text-slate-400 hover:text-white">
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>
            )}

            {/* Filter and Search Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-800/80 p-3.5 rounded-xl border border-slate-700/80 text-xs">
                <div className="flex items-center gap-3 flex-1 min-w-[260px]">
                    <div className="relative flex-1 max-w-md">
                        <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[16px]">search</span>
                        <input
                            type="text"
                            placeholder="Search client database, QB account, or company..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>

                    <div className="flex items-center gap-1.5">
                        <span className="text-slate-400 font-medium">Environment:</span>
                        <select
                            value={envFilter}
                            onChange={e => setEnvFilter(e.target.value)}
                            className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-200 focus:outline-none focus:border-blue-500"
                        >
                            <option value="ALL">All Environments</option>
                            <option value="sandbox">Sandbox Only</option>
                            <option value="production">Production Only</option>
                        </select>
                    </div>
                </div>

                <div className="text-slate-400 font-medium">
                    Showing <strong>{filteredConnections.length}</strong> active connections ({configs.length} table sync pipelines)
                </div>
            </div>

            {/* Connection Rows List (Single-Line Condensed View) */}
            {filteredConnections.length === 0 ? (
                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-12 text-center text-slate-400">
                    <span className="material-symbols-outlined text-5xl text-slate-500 mb-2">account_tree</span>
                    <h3 className="text-lg font-bold text-slate-200">No Integration Connections Configured</h3>
                    <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                        Link a saved client database profile (e.g. `pbsdata00`) to a QuickBooks company to begin synchronizing standard ERP entities.
                    </p>
                    <div className="mt-4 flex justify-center gap-3">
                        <button
                            onClick={() => setShowCatalogStudio(true)}
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                        >
                            <span className="material-symbols-outlined text-[16px]">menu_book</span>
                            Data Catalog &amp; Connections
                        </button>
                        <button
                            onClick={() => {
                                setPipelineDefaults({});
                                setPipelineConfig(null);
                                setShowPipelineModal(true);
                            }}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                            + Create First Pipeline
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredConnections.map(conn => {
                        const isExpanded = !!expandedConnections[conn.groupKey];
                        const isSandbox = conn.accountEnvironment === 'sandbox';
                        const isConnected = conn.accountConnected;
                        const activePipelinesCount = conn.pipelines.filter(p => p.IsActive).length;

                        const availableTargetEntities = QUICKBOOKS_TARGET_ENTITIES.map(e => ({
                            name: e.name || e.entity,
                            entity: e.entity || e.name,
                            label: e.label,
                            supported: true,
                            icon: e.icon,
                            defaultTable: e.defaultTable
                        }));
                        const mappedEntityTypes = new Set(conn.pipelines.map(p => (p.TargetQBEntity || p.EntityType || '').toLowerCase()));

                        return (
                            <div
                                key={conn.groupKey}
                                className={`relative bg-slate-800 border rounded-xl overflow-hidden shadow-md transition-all ${isExpanded ? 'border-blue-500/50 ring-1 ring-blue-500/20' : 'border-slate-700 hover:border-slate-600'}`}
                            >
                                {/* Yellow Cornertag for Sandbox Accounts */}
                                {isSandbox && (
                                    <div className="absolute top-0 right-0 overflow-hidden w-20 h-20 pointer-events-none z-10">
                                        <div className="bg-amber-400 text-slate-950 text-[9px] font-black tracking-widest text-center py-0.5 shadow-md transform rotate-45 translate-x-6 translate-y-3 uppercase">
                                            SANDBOX
                                        </div>
                                    </div>
                                )}

                                {/* Single-Line Main Row */}
                                <div
                                    onClick={() => toggleConnectionExpand(conn.groupKey)}
                                    className="p-4 flex flex-wrap items-center justify-between gap-4 cursor-pointer select-none bg-slate-800 hover:bg-slate-750 transition-colors"
                                >
                                    {/* Left: Indicator, Client DB, QB Account */}
                                    <div className="flex items-center gap-3.5 min-w-[280px] flex-1">
                                        {/* Status Pulsing Dot */}
                                        <div
                                            className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 border border-slate-700"
                                            title={isConnected ? 'Connected & Authorized' : 'Authorization Required'}
                                        >
                                            <span className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)] animate-pulse' : 'bg-amber-400'}`}></span>
                                        </div>

                                        {/* Client / Database Name */}
                                        <div className="flex items-center gap-2">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-bold text-white font-mono">
                                                        {conn.sourceDatabase}
                                                    </span>
                                                    <span className="px-2 py-0.5 text-[10px] rounded bg-slate-900 text-slate-300 border border-slate-700 font-mono truncate max-w-[220px]">
                                                        {conn.profileDisplayName}
                                                    </span>
                                                    <span className="text-slate-500">&rarr;</span>
                                                    <span className="text-sm font-bold text-emerald-300">
                                                        {conn.accountName}
                                                    </span>
                                                    {conn.companyName && (
                                                        <span className="text-xs text-slate-400 font-medium">
                                                            ({conn.companyName})
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-slate-400 mt-0.5 flex flex-col gap-1">
                                                    <div>
                                                        {conn.pipelines.length === 0 ? (
                                                             <span className="text-amber-400">No table pipelines linked</span>
                                                        ) : (
                                                            <span>
                                                                <strong>{activePipelinesCount}</strong> of <strong>{conn.pipelines.length}</strong> table syncs active &bull; {conn.pipelines.map(p => p.EntityType).join(', ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* Target Entities Pills with Mapped Field Counts */}
                                                    <div className="flex items-center gap-1.5 flex-wrap pt-0.5" onClick={e => e.stopPropagation()}>
                                                        <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1 mr-0.5">
                                                            <span className="material-symbols-outlined text-[13px] text-emerald-400">task_alt</span>
                                                            QB Targets:
                                                        </span>
                                                        {availableTargetEntities.map(ent => {
                                                            const entName = typeof ent === 'string' ? ent : (ent.name || ent.entity || '');
                                                            const matchedPipeline = findPipelineForEntity(conn.pipelines, entName);
                                                            const mappedCount = matchedPipeline ? getMappedFieldCount(matchedPipeline) : 0;
                                                            const firstTarget = conn.pipelines[0]?.TargetQBEntity || conn.pipelines[0]?.EntityType || 'Customer';
                                                            const isSelected = isExpanded && (
                                                                selectedEntityByGroup[conn.groupKey] === entName || 
                                                                (!selectedEntityByGroup[conn.groupKey] && String(firstTarget).toLowerCase() === String(entName).toLowerCase())
                                                            );

                                                            if (matchedPipeline && mappedCount > 0) {
                                                                return (
                                                                    <button
                                                                        key={entName}
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); handlePillClick(conn, entName); }}
                                                                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-all flex items-center gap-1 bg-emerald-950/90 text-emerald-300 border-emerald-600/80 hover:bg-emerald-900 shadow-sm ${
                                                                            isSelected ? 'ring-2 ring-emerald-400 border-emerald-400 scale-105' : ''
                                                                        }`}
                                                                        title={`${entName}: ${mappedCount} field(s) mapped & syncing. Click to expand and manage.`}
                                                                    >
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                                                        <span>{entName}</span>
                                                                        <span className="px-1 py-0.2 rounded bg-emerald-850 text-emerald-200 text-[9px] font-mono">
                                                                            ({mappedCount})
                                                                        </span>
                                                                    </button>
                                                                );
                                                            }

                                                            if (matchedPipeline && mappedCount === 0) {
                                                                return (
                                                                    <button
                                                                        key={entName}
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); handlePillClick(conn, entName); }}
                                                                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all flex items-center gap-1 bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-750 ${
                                                                            isSelected ? 'ring-2 ring-blue-400 border-blue-400 scale-105' : ''
                                                                        }`}
                                                                        title={`${entName}: Pipeline exists but 0 fields mapped. Click to configure.`}
                                                                    >
                                                                        <span>{entName}</span>
                                                                        <span className="px-1 py-0.2 rounded bg-slate-900 text-slate-400 text-[9px] font-mono">(0)</span>
                                                                    </button>
                                                                );
                                                            }

                                                            return (
                                                                <button
                                                                    key={entName}
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); handlePillClick(conn, entName); }}
                                                                    className="px-2 py-0.5 rounded-full text-[10px] font-medium border border-dashed border-slate-700/80 text-slate-400 hover:text-white hover:border-slate-500 bg-slate-900/40 hover:bg-slate-800 transition-all flex items-center gap-1"
                                                                    title={`No pipeline configured for ${entName}. Click to initialize.`}
                                                                >
                                                                    <span className="material-symbols-outlined text-[12px] text-slate-500">add</span>
                                                                    <span>{entName}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Quick Actions & Chevron */}
                                    <div className="flex items-center gap-2 z-20" onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={() => setApplyMappingsGroup(conn)}
                                            className="px-2.5 py-1 bg-indigo-900/60 hover:bg-indigo-800/80 text-indigo-200 border border-indigo-700/60 rounded text-xs font-semibold flex items-center gap-1 transition-colors shadow-sm"
                                            title="Apply standard field mappings en masse across this connection group"
                                        >
                                            <span className="material-symbols-outlined text-[15px] text-emerald-400">bolt</span>
                                            Apply Standards
                                        </button>

                                        <button
                                            onClick={() => setEditingGroup(conn)}
                                            className="px-2.5 py-1 bg-slate-900 hover:bg-slate-700 text-slate-200 rounded text-xs font-semibold flex items-center gap-1 transition-colors border border-slate-700"
                                            title="Edit shared SQL Server instance, network interface, database, and QB account for this group"
                                        >
                                            <span className="material-symbols-outlined text-[15px] text-amber-400">tune</span>
                                            Edit Connection
                                        </button>

                                        <button
                                            onClick={() => setDetailsAccount(conn.account || { AccountID: conn.accountId, AccountName: conn.accountName, Environment: conn.accountEnvironment, IsConnected: conn.accountConnected })}
                                            className="px-2.5 py-1 bg-slate-900 hover:bg-slate-700 text-slate-300 rounded text-xs font-semibold flex items-center gap-1 transition-colors border border-slate-700"
                                            title="View Client ID, Token Expiration & Re-Auth"
                                        >
                                            <span className="material-symbols-outlined text-[15px] text-emerald-400">key</span>
                                            Credentials
                                        </button>

                                        <button
                                            onClick={() => setLogsAccount({ accountId: conn.accountId, title: `${conn.sourceDatabase} ➔ ${conn.accountName}` })}
                                            className="px-2.5 py-1 bg-slate-900 hover:bg-slate-700 text-slate-300 rounded text-xs font-semibold flex items-center gap-1 transition-colors border border-slate-700"
                                            title="View Execution Audit Logs"
                                        >
                                            <span className="material-symbols-outlined text-[15px] text-blue-400">receipt_long</span>
                                            Audit Logs
                                        </button>

                                        <button
                                            onClick={() => toggleConnectionExpand(conn.groupKey)}
                                            className="p-1 text-slate-400 hover:text-white rounded hover:bg-slate-700 transition-colors ml-1"
                                            title={isExpanded ? 'Collapse' : 'Expand Details'}
                                        >
                                            <span className="material-symbols-outlined text-[20px]">
                                                {isExpanded ? 'expand_less' : 'expand_more'}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded Details Panel: Focused Pipeline Drawer */}
                                {isExpanded && (() => {
                                    const rawSelected = selectedEntityByGroup[conn.groupKey];
                                    const activeEntity = typeof rawSelected === 'string'
                                        ? rawSelected
                                        : (rawSelected?.name || rawSelected?.entity || conn.pipelines[0]?.TargetQBEntity || conn.pipelines[0]?.EntityType || 'Customer');
                                    const activePipeline = findPipelineForEntity(conn.pipelines, activeEntity);

                                    return (
                                        <div className="border-t border-slate-700/80 bg-slate-900/95 p-4 transition-all">
                                            {activePipeline ? (
                                                <FieldMappingDesigner
                                                    key={`pipeline_${conn.groupKey}_${activePipeline.ConfigID}_${activePipeline.TargetQBEntity || activeEntity}`}
                                                    mode="pipeline"
                                                    inline={true}
                                                    config={activePipeline}
                                                    token={token}
                                                    onSaved={loadData}
                                                    onPipelineUpdated={loadData}
                                                    onClose={() => toggleConnectionExpand(conn.groupKey)}
                                                />
                                            ) : (
                                                /* Empty State: Target entity has no pipeline yet */
                                                <div className="bg-slate-850/90 border border-dashed border-slate-700 rounded-xl p-8 text-center text-slate-400 space-y-4">
                                                    <div className="w-14 h-14 rounded-2xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center text-blue-400 mx-auto shadow-inner">
                                                        <span className="material-symbols-outlined text-3xl">add_link</span>
                                                    </div>
                                                    <div className="max-w-md mx-auto space-y-1">
                                                        <h4 className="text-lg font-bold text-white">No Pipeline for {activeEntity}</h4>
                                                        <p className="text-xs text-slate-400">
                                                            There is currently no synchronization pipeline configured for target entity <strong className="text-emerald-300">{activeEntity}</strong> between <strong className="text-slate-200">{conn.sourceDatabase}</strong> and <strong className="text-slate-200">{conn.accountName}</strong>.
                                                        </p>
                                                    </div>
                                                    <div className="pt-2">
                                                        <button
                                                            onClick={() => handleInitializePipeline(conn, activeEntity)}
                                                            disabled={loading}
                                                            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-all shadow-md inline-flex items-center gap-2 hover:shadow-blue-900/40 hover:scale-[1.02] active:scale-[0.98]"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">add_task</span>
                                                            Initialize {activeEntity} Pipeline from Standard Preset
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Field Mapping Modal */}
            {mappingConfig && (
                <FieldMappingModal
                    token={token}
                    config={mappingConfig}
                    onClose={() => setMappingConfig(null)}
                    onSaved={loadData}
                />
            )}

            {/* Dry Run Diff & Live Sync Modal */}
            {diffConfig && (
                <DryRunDiffModal
                    token={token}
                    config={diffConfig}
                    onClose={() => setDiffConfig(null)}
                    onSyncCompleted={loadData}
                />
            )}

            {/* Connection Credentials & Token Diagnostics Modal */}
            {detailsAccount && (
                <ConnectionDetailsModal
                    token={token}
                    account={detailsAccount}
                    onClose={() => setDetailsAccount(null)}
                    onAccountUpdated={loadData}
                />
            )}

            {/* Connection Audit Logs Modal */}
            {logsAccount && (
                <ConnectionLogsModal
                    token={token}
                    accountId={logsAccount.accountId}
                    connectionTitle={logsAccount.title}
                    onClose={() => setLogsAccount(null)}
                />
            )}

            {/* Pipeline Configuration Modal (Create or Edit) */}
            {showPipelineModal && (
                <PipelineConfigModal
                    token={token}
                    config={pipelineConfig}
                    accounts={accounts}
                    dbProfiles={dbProfiles}
                    initialDefaults={pipelineDefaults}
                    onClose={() => {
                        setShowPipelineModal(false);
                        setPipelineConfig(null);
                        setPipelineDefaults({});
                    }}
                    onSaved={loadData}
                />
            )}

            {/* Environmental Integration Settings Modal */}
            {showSettingsModal && (
                <IntegrationSettingsModal
                    token={token}
                    onClose={() => setShowSettingsModal(false)}
                    onSettingsUpdated={loadData}
                    onOpenStandardMappings={() => setShowStandardMappingsModal(true)}
                />
            )}

            {/* Standard Field Mappings Preset Manager Modal */}
            {showStandardMappingsModal && (
                <StandardMappingsModal
                    token={token}
                    onClose={() => setShowStandardMappingsModal(false)}
                    onSaved={loadData}
                />
            )}

            {/* En Masse Group Application Modal */}
            {applyMappingsGroup && (
                <ApplyStandardMappingsModal
                    token={token}
                    group={applyMappingsGroup}
                    onClose={() => setApplyMappingsGroup(null)}
                    onApplied={() => {
                        setApplyMappingsGroup(null);
                        loadData();
                    }}
                />
            )}

            {/* Passport Data Dictionary & Field Tagger Modal */}
            {showDictionaryModal && (
                <DataDictionaryModal
                    token={token}
                    onClose={() => setShowDictionaryModal(false)}
                />
            )}

            {/* Data Catalog & Connection Studio Modal */}
            {showCatalogStudio && (
                <DataCatalogStudio
                    token={token}
                    onClose={() => setShowCatalogStudio(false)}
                />
            )}

            {/* Edit Connection Group Modal */}
            {editingGroup && (
                <EditConnectionGroupModal
                    token={token}
                    group={editingGroup}
                    dbProfiles={dbProfiles}
                    accounts={accounts}
                    onClose={() => setEditingGroup(null)}
                    onSaved={() => {
                        setEditingGroup(null);
                        loadData();
                    }}
                />
            )}
            {/* Centralized Global Dialogs (Alert & Confirm Modals) */}
            <GlobalDialogs />
        </div>
    );
}
